import { Controller } from "@hotwired/stimulus"
import { nativeHaptic } from "../utils/native_bridge"

// Touch swipe-to-mark-read for Activity feed rows (touch pointers only).
// Right-swiping an unread row past the threshold marks it read immediately —
// faster than waiting out the auto-read dwell — via an activity-swipe:read
// event handled by activity-seen (which persists through the same batched
// mark_seen endpoint and updates badges). The blue action layer is a
// fixed-position element sized to the revealed gap; the page can't scroll
// while a swipe is claimed, so fixed coordinates stay valid.
export default class extends Controller {
    CLAIM_PX = 24
    THRESHOLD_RATIO = 0.35

    initialize() {
        this._onPointerDown = this._pointerDown.bind(this)
        this._onPointerMove = this._pointerMove.bind(this)
        this._onPointerUp = this._pointerUp.bind(this)
        this._onPointerCancel = this._pointerCancel.bind(this)
    }

    connect() {
        this._swipe = null
        this.element.addEventListener("pointerdown", this._onPointerDown)
        this.element.addEventListener("pointermove", this._onPointerMove)
        this.element.addEventListener("pointerup", this._onPointerUp)
        this.element.addEventListener("pointercancel", this._onPointerCancel)
    }

    disconnect() {
        this.element.removeEventListener("pointerdown", this._onPointerDown)
        this.element.removeEventListener("pointermove", this._onPointerMove)
        this.element.removeEventListener("pointerup", this._onPointerUp)
        this.element.removeEventListener("pointercancel", this._onPointerCancel)
        if (this._swipe) this._cleanup(this._swipe)
    }

    _pointerDown(e) {
        if (e.pointerType !== "touch" || !e.isPrimary || this._swipe) return
        let row = e.target.closest(".activity-row.is-unread")
        if (!row || !this.element.contains(row)) return
        this._swipe = { row: row, pointerId: e.pointerId, startX: e.clientX, startY: e.clientY, width: row.offsetWidth, rect: null, layer: null, claimed: false, dx: 0, past: false }
    }

    _pointerMove(e) {
        let s = this._swipe
        if (!s || e.pointerId !== s.pointerId) return
        let dx = e.clientX - s.startX
        let dy = e.clientY - s.startY

        if (!s.claimed) {
            if (Math.abs(dy) > 14 && Math.abs(dy) > Math.abs(dx)) {
                this._swipe = null
                return
            }
            if (dx <= -this.CLAIM_PX) {
                this._swipe = null
                return
            }
            if (dx < this.CLAIM_PX || dx < Math.abs(dy) * 1.2) return
            s.claimed = true
            s.rect = s.row.getBoundingClientRect()
            s.layer = this._buildLayer(s)
            s.row.classList.add("is-activity-swiping")
            try { s.row.setPointerCapture(e.pointerId) } catch { /* detached row */ }
        }

        e.preventDefault()
        s.dx = Math.max(dx, 0)
        s.row.style.transform = `translateX(${s.dx}px)`
        s.layer.style.width = `${s.dx}px`
        let past = s.dx >= s.width * this.THRESHOLD_RATIO
        if (past !== s.past) {
            s.past = past
            s.layer.classList.toggle("is-past-threshold", past)
            if (past) nativeHaptic("light")
        }
    }

    _pointerUp(e) {
        let s = this._swipe
        if (!s || e.pointerId !== s.pointerId) return
        this._swipe = null
        if (!s.claimed) return
        this._suppressNextClick()
        if (s.dx >= s.width * this.THRESHOLD_RATIO) {
            nativeHaptic("medium")
            this.dispatch("read", { detail: { row: s.row } })
        }
        this._springBack(s)
    }

    _pointerCancel(e) {
        let s = this._swipe
        if (!s || e.pointerId !== s.pointerId) return
        this._swipe = null
        if (s.claimed) this._springBack(s)
    }

    _buildLayer(s) {
        let layer = document.createElement("div")
        layer.className = "activity-swipe-layer"
        layer.setAttribute("data-turbo-temporary", "true")
        layer.style.top = `${s.rect.top}px`
        layer.style.left = `${s.rect.left}px`
        layer.style.height = `${s.rect.height}px`
        layer.style.width = "0px"
        layer.innerHTML = `<i class="bi bi-check2"></i><span>Read</span>`
        document.body.appendChild(layer)
        return layer
    }

    _springBack(s) {
        s.row.classList.add("is-activity-swipe-settling")
        s.layer.classList.add("is-settling")
        s.row.style.transform = ""
        s.layer.style.width = "0px"
        setTimeout(() => this._cleanup(s), 220)
    }

    _cleanup(s) {
        if (s.layer) s.layer.remove()
        s.row.classList.remove("is-activity-swiping", "is-activity-swipe-settling")
        s.row.style.transform = ""
    }

    // A completed swipe must not double as a tap on the row's buttons/links.
    _suppressNextClick() {
        let swallow = (e) => {
            e.stopPropagation()
            e.preventDefault()
        }
        this.element.addEventListener("click", swallow, { capture: true, once: true })
        setTimeout(() => this.element.removeEventListener("click", swallow, { capture: true }), 400)
    }
}
